import {useRef} from 'react';
import * as React from 'react';

import {LOCALE} from 'features/ui/enums';
import {ResidenceType} from 'features/search/filters/enums';
import {getCurrentLocale} from 'features/services/utils/env-helper';
import {renderInhabitDate} from 'features/services/utils/date-helpers';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {getConstructionFormName} from 'features/services/utils/name-helpers';

import './style.scss';

import { Facts } from '.';
import {QuickFactsItem} from './item';

interface Props {
    showMore?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- viewModel varies by page type
    viewModel: any;

    addressString: string;
    areaString: string;
    energyMarking: string;
    roomString: string;
    renderAreaInfoNorway: (areaType: string) => () => void;
}

export const RentalQuickFacts: React.FunctionComponent<Props> = ({ 
    showMore, viewModel, 
    addressString, areaString, energyMarking, roomString, 
    renderAreaInfoNorway 
}) => {
    const areaButtonRef = useRef<HTMLButtonElement>(null);
    const numberOfVisibleItems = 5;

    const facts : Facts[] = [
        {key: 'address', devCounter: '01',//(26)  
            label: getFormattedMessage('jm.components.QuickFacts.address'), 
            fact: addressString, 
            condition: addressString 
        },
        {key: 'constructionForm', devCounter: '02',//(21)  
            label: getFormattedMessage('jm.components.QuickFacts.constructionForm'), 
            fact: getConstructionFormName(viewModel.constructionForm as ResidenceType), 
            condition: viewModel.constructionForm 
        },
        {key: 'energyMarking', devCounter: '03',//(19)  
            label: getFormattedMessage('jm.components.QuickFacts.energyMarking'), 
            fact: energyMarking, 
            condition: energyMarking && (!viewModel.swanLabel || getCurrentLocale() === LOCALE.NO)
        },
        {key: 'inhabitDate', devCounter: '04',//(23)  
            label: getFormattedMessage('jm.components.QuickFacts.inhabitDate'), 
            fact: renderInhabitDate(viewModel.inhabitDate, viewModel.inhabitDateEnd, viewModel.inhabitDateDetermined, false, false, viewModel.inhabitDateRenderFull), 
            condition: viewModel.inhabitDate 
        },
        {key: 'minMaxArea', devCounter: '05',//(11)  
            label: getCurrentLocale() === LOCALE.NO
                ? getFormattedMessage('jm.components.QuickFacts.noBRAi')
                : getFormattedMessage('jm.components.QuickFacts.area'), 
            fact: areaString, 
            onClick: getCurrentLocale() === LOCALE.NO
                ? renderAreaInfoNorway('bra-i')
                : undefined,
            ref: areaButtonRef,
            condition: Boolean(areaString) 
        },
        {key: 'minMaxRooms', devCounter: '06',//(06)  
            label: getFormattedMessage('jm.components.QuickFacts.rooms'), 
            fact: roomString, 
            condition: roomString
        },
        {key: 'numberOfObjects', devCounter: '07',//(18)  
            label: getFormattedMessage('jm.components.QuickFacts.numberOfObjects'), 
            fact: viewModel.nrOfObjects, 
            condition: (viewModel.nrOfObjects !== 0 && viewModel.nrOfObjects !== null && typeof viewModel.nrOfObjects !== 'undefined') 
        },
        {key: 'swanLabel', devCounter: '08',//(20)  
            label: getFormattedMessage('jm.components.QuickFacts.swanLabel'), 
            fact: getFormattedMessage('jm.components.QuickFacts.yes'), 
            condition: viewModel.swanLabel 
        }
    ];

    const renderFactsByOrder = (factOrder: string[]) => {
        let factIndex = 0;
        return factOrder.map((key, index) => {
            const fact = facts.find(f => f.key === key);
            if (!fact || !fact.condition) return null;
            factIndex++;

            return (
                <QuickFactsItem
                    key={fact.key}
                    index={index.toString()}
                    devCounter={fact.devCounter}
                    itemLabel={fact.label}
                    itemFact={fact.fact}
                    isHidden={!showMore && factIndex > numberOfVisibleItems} 
                />
            );
        })
        .filter(Boolean);
    };

    const order = [
        'minMaxRooms',
        'minMaxArea',
        'numberOfObjects',
        'constructionForm',
        'inhabitDate',
        'address',
        'energyMarking',
        'swanLabel',
    ];

    return (
        <dl className="quick-facts__list">
            {renderFactsByOrder(order)}
        </dl>
    )
};